import { type SVGProps } from 'react'
import { Radio as RadioPrimitive } from '@base-ui/react/radio'
import { RotateCcw, CircleCheck, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { IconLayoutCompact } from '@/assets/custom/icon-layout-compact'
import { IconLayoutDefault } from '@/assets/custom/icon-layout-default'
import { IconLayoutFull } from '@/assets/custom/icon-layout-full'
import { IconSidebarFloating } from '@/assets/custom/icon-sidebar-floating'
import { IconSidebarInset } from '@/assets/custom/icon-sidebar-inset'
import { IconSidebarSidebar } from '@/assets/custom/icon-sidebar-sidebar'
import { IconThemeDark } from '@/assets/custom/icon-theme-dark'
import { IconThemeLight } from '@/assets/custom/icon-theme-light'
import { IconThemeSystem } from '@/assets/custom/icon-theme-system'
import { fonts } from '@/shared/config/fonts'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { cn } from '@/shared/lib/utils'
import { useFont } from '@/context/font-provider'
import { Collapsible, useLayout } from '@/context/layout-provider'
import { useTheme } from '@/context/theme-provider'
import { Button, buttonVariants } from '@/components/ui/button'
import { RadioGroup } from '@/components/ui/radio-group'
import { useSidebar } from '@/components/ui/sidebar'

type Font = (typeof fonts)[number]

export function ThemeForm() {
  const { t } = useTranslation('common')
  const { data: session } = useGetUserSession()
  const { resetTheme } = useTheme()
  const { resetLayout } = useLayout()
  const { resetFont } = useFont()
  const isArtemAdmin = session?.isArtemAdmin === true

  const handleReset = () => {
    resetTheme()
    resetLayout()
    if (isArtemAdmin) {
      resetFont()
    }
  }

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <div className='space-y-6 overflow-y-auto px-4'>
          <ThemeConfig />
          <FontConfig />
          <SidebarConfig />
          <LayoutConfig />
          <Button
            variant='destructive'
            onClick={handleReset}
            aria-label={t('uiSettings.drawer.resetAllAriaLabel')}
          >
            {t('uiSettings.drawer.resetButton')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function SectionTitle({
  title,
  showReset = false,
  onReset,
  className,
}: {
  title: string
  showReset?: boolean
  onReset?: () => void
  className?: string
}) {
  return (
    <div
      className={cn(
        'mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground',
        className
      )}
    >
      {title}
      {showReset && onReset && (
        <Button
          size='icon'
          variant='secondary'
          className='size-4 rounded-full'
          onClick={onReset}
        >
          <RotateCcw className='size-3' />
        </Button>
      )}
    </div>
  )
}

function ThemeOptionItem({
  item,
  isTheme = false,
}: {
  item: {
    value: string
    label: string
    icon: (props: SVGProps<SVGSVGElement>) => React.ReactElement
  }
  isTheme?: boolean
}) {
  const { t } = useTranslation('common')

  return (
    <RadioPrimitive.Root
      value={item.value}
      className={cn('group outline-none', 'transition duration-200 ease-in')}
      aria-label={t('uiSettings.aria.selectOption', { label: item.label })}
      aria-describedby={`${item.value}-description`}
    >
      <div
        className={cn(
          'relative rounded-[6px] ring-[1px] ring-border',
          'group-data-[checked]:shadow-2xl group-data-[checked]:ring-primary',
          'group-focus-visible:ring-2'
        )}
        role='img'
        aria-hidden='false'
        aria-label={t('uiSettings.aria.optionPreview', { label: item.label })}
      >
        <CircleCheck
          className={cn(
            'size-6 fill-primary stroke-white',
            'group-data-[unchecked]:hidden',
            'absolute top-0 right-0 translate-x-1/2 -translate-y-1/2'
          )}
          aria-hidden='true'
        />
        <item.icon
          className={cn(
            !isTheme &&
              'fill-primary stroke-primary group-data-[unchecked]:fill-muted-foreground group-data-[unchecked]:stroke-muted-foreground'
          )}
          aria-hidden='true'
        />
      </div>
      <div
        className='mt-1 text-xs'
        id={`${item.value}-description`}
        aria-live='polite'
      >
        {item.label}
      </div>
    </RadioPrimitive.Root>
  )
}

function ThemeConfig() {
  const { t } = useTranslation('common')
  const { defaultTheme, theme, setTheme } = useTheme()
  return (
    <div>
      <SectionTitle
        title={t('uiSettings.sections.theme')}
        showReset={theme !== defaultTheme}
        onReset={() => setTheme(defaultTheme)}
      />
      <RadioGroup
        value={theme}
        onValueChange={setTheme}
        className='grid w-full max-w-md grid-cols-3 gap-4'
        aria-label={t('uiSettings.aria.selectThemePreference')}
        aria-describedby='theme-description'
      >
        {[
          {
            value: 'system',
            label: t('uiSettings.themeOptions.system'),
            icon: IconThemeSystem,
          },
          {
            value: 'light',
            label: t('uiSettings.themeOptions.light'),
            icon: IconThemeLight,
          },
          {
            value: 'dark',
            label: t('uiSettings.themeOptions.dark'),
            icon: IconThemeDark,
          },
        ].map((item) => (
          <ThemeOptionItem key={item.value} item={item} isTheme />
        ))}
      </RadioGroup>
      <div id='theme-description' className='sr-only'>
        {t('uiSettings.aria.themeDescription')}
      </div>
    </div>
  )
}

function FontConfig() {
  const { t } = useTranslation('common')
  const { data: session } = useGetUserSession()
  const { font, setFont, resetFont } = useFont()
  const defaultFont = fonts[0]

  if (session?.isArtemAdmin !== true) {
    return null
  }

  return (
    <div>
      <SectionTitle
        title={t('uiSettings.sections.font')}
        showReset={font !== defaultFont}
        onReset={resetFont}
      />
      <div className='relative w-max'>
        <select
          id='dashboard-font'
          value={font}
          onChange={(event) => {
            setFont(event.target.value as Font)
          }}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'w-[200px] appearance-none font-normal capitalize',
            'dark:bg-background dark:hover:bg-background'
          )}
          aria-label={t('uiSettings.aria.selectFont')}
          aria-describedby='font-description'
        >
          {fonts.map((fontName) => (
            <option key={fontName} value={fontName}>
              {fontName}
            </option>
          ))}
        </select>
        <ChevronDown className='pointer-events-none absolute end-3 top-2.5 h-4 w-4 opacity-50' />
      </div>
      <p
        id='font-description'
        className='mt-2 text-sm text-muted-foreground'
      >
        {t('uiSettings.fontDescription')}
      </p>
    </div>
  )
}

function SidebarConfig() {
  const { t } = useTranslation('common')
  const { defaultVariant, variant, setVariant } = useLayout()
  return (
    <div className='max-md:hidden'>
      <SectionTitle
        title={t('uiSettings.sections.sidebar')}
        showReset={defaultVariant !== variant}
        onReset={() => setVariant(defaultVariant)}
      />
      <RadioGroup
        value={variant}
        onValueChange={setVariant}
        className='grid w-full max-w-md grid-cols-3 gap-4'
        aria-label={t('uiSettings.aria.selectSidebarStyle')}
        aria-describedby='sidebar-description'
      >
        {[
          {
            value: 'inset',
            label: t('uiSettings.sidebarOptions.inset'),
            icon: IconSidebarInset,
          },
          {
            value: 'floating',
            label: t('uiSettings.sidebarOptions.floating'),
            icon: IconSidebarFloating,
          },
          {
            value: 'sidebar',
            label: t('uiSettings.sidebarOptions.sidebar'),
            icon: IconSidebarSidebar,
          },
        ].map((item) => (
          <ThemeOptionItem key={item.value} item={item} />
        ))}
      </RadioGroup>
      <div id='sidebar-description' className='sr-only'>
        {t('uiSettings.aria.sidebarDescription')}
      </div>
    </div>
  )
}

function LayoutConfig() {
  const { t } = useTranslation('common')
  const { open, setOpen } = useSidebar()
  const { defaultCollapsible, collapsible, setCollapsible } = useLayout()

  const radioState = open ? 'default' : collapsible

  return (
    <div className='max-md:hidden'>
      <SectionTitle
        title={t('uiSettings.sections.layout')}
        showReset={radioState !== 'default'}
        onReset={() => {
          setOpen(true)
          setCollapsible(defaultCollapsible)
        }}
      />
      <RadioGroup
        value={radioState}
        onValueChange={(value) => {
          if (value === 'default') {
            setOpen(true)
            return
          }
          setOpen(false)
          setCollapsible(value as Collapsible)
        }}
        className='grid w-full max-w-md grid-cols-3 gap-4'
        aria-label={t('uiSettings.aria.selectLayoutStyle')}
        aria-describedby='layout-description'
      >
        {[
          {
            value: 'default',
            label: t('uiSettings.layoutOptions.default'),
            icon: IconLayoutDefault,
          },
          {
            value: 'icon',
            label: t('uiSettings.layoutOptions.compact'),
            icon: IconLayoutCompact,
          },
          {
            value: 'offcanvas',
            label: t('uiSettings.layoutOptions.fullLayout'),
            icon: IconLayoutFull,
          },
        ].map((item) => (
          <ThemeOptionItem key={item.value} item={item} />
        ))}
      </RadioGroup>
      <div id='layout-description' className='sr-only'>
        {t('uiSettings.aria.layoutDescription')}
      </div>
    </div>
  )
}
